import { HttpClient } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { API_BASE_URL } from "src/swagger/api.client.generated";

/** Matches BrainChanger's standard { resultStatus, responseData } API envelope. */
interface ResponseEnvelope<T> {
  resultStatus?: { statusCode?: number; errorText?: string };
  responseData?: T;
}

interface CreateCheckoutSessionResponse {
  url?: string;
}

interface CreateBillingPortalSessionResponse {
  url?: string;
}

interface VerifySessionResponse {
  verified?: boolean;
  subscriptionActive?: boolean;
}

export interface SubscriptionStatusResponse {
  status?: string;
  isActive?: boolean;
  // FLIP-248: drive the Settings "Subscription" section. Dates arrive as ISO strings
  // (plain HttpClient JSON parsing - see the class comment for why this isn't the
  // NSwag-generated client, which would parse these into Date objects instead).
  planId?: string;
  purchaseDate?: string;
  termEndDate?: string;
  isLifetime?: boolean;
  // FLIP-261: whether this plan has a real Stripe Subscription to cancel, and whether a
  // cancellation is already pending (access lasts until termEndDate either way).
  isRecurring?: boolean;
  cancelAtPeriodEnd?: boolean;
}

interface CancelSubscriptionResponse {
  cancelAtPeriodEnd?: boolean;
  periodEndUtc?: string;
}

interface ResumeSubscriptionResponse {
  cancelAtPeriodEnd?: boolean;
  periodEndUtc?: string;
}

interface CheckAccountSubscriptionResponse {
  accountExists?: boolean;
  hasActiveSubscription?: boolean;
}

// FLIP-251: one row of billing history. Source-agnostic (Stripe Charge today, Invoice once
// recurring subscriptions exist - see PaymentsController.BillingHistory) so this shape doesn't
// need to change either way.
export interface BillingHistoryItem {
  dateUtc?: string;
  amount?: number;
  currency?: string;
  planId?: string;
  receiptUrl?: string;
  // Set only for a $0 promo-code redemption, which Stripe never turns into a Charge/Invoice -
  // see PaymentsController.BillingHistory. Null for every ordinary paid row.
  promotionCode?: string;
}

/**
 * Hand-written rather than NSwag-generated: PaymentsController/StripeWebhookController
 * are new and this sandbox has no reachable SQL Server for BrainChanger.API to start
 * against (Startup.cs runs Database.Migrate() on boot and aborts if it can't reach a
 * database), so `npm run nswag` can't hit a live /swagger/v1/swagger.json here.
 * Regenerate api.client.generated.ts and switch to the generated `PaymentsService`
 * once run against a real backend - see changelog/2026-07-11-stripe-checkout-integration.md.
 */
@Injectable({ providedIn: "root" })
export class PaymentsService {
  // Cached so SubscriptionGuard doesn't round-trip on every navigation once we know
  // the account is active. Set directly by payment-success right after a successful
  // verify, and cleared on logout via clearCache().
  private cachedActive: boolean | null = null;

  // Reactive mirror of cachedActive so the app shell (showNav, the authenticated-init
  // fan-out) can gate on a *confirmed* active subscription rather than mere token
  // presence. Starts false: nothing authenticated runs until a check confirms it.
  private subscriptionActiveSource = new BehaviorSubject<boolean>(false);
  subscriptionActive$ = this.subscriptionActiveSource.asObservable();

  /** True only once a subscription check has confirmed an active subscription. */
  get isConfirmedActive(): boolean {
    return this.cachedActive === true;
  }

  constructor(
    private http: HttpClient,
    @Inject(API_BASE_URL) private apiBaseUrl: string
  ) {}

  markSubscriptionActive() {
    this.cachedActive = true;
    this.subscriptionActiveSource.next(true);
  }

  clearCache() {
    this.cachedActive = null;
    this.subscriptionActiveSource.next(false);
  }

  async isSubscriptionActive(): Promise<boolean> {
    const status = await this.checkSubscriptionStatus();
    return !!status.isActive;
  }

  /**
   * Like isSubscriptionActive(), but also returns the raw status string (e.g. "expired")
   * so callers - SubscriptionGuard in particular - can distinguish why an account is
   * inactive rather than just that it is.
   */
  async checkSubscriptionStatus(): Promise<SubscriptionStatusResponse> {
    if (this.cachedActive) return { status: "active", isActive: true };
    const status = await this.subscriptionStatus();
    this.cachedActive = status.isActive;
    this.subscriptionActiveSource.next(!!status.isActive);
    return status;
  }

  async createCheckoutSession(planId: string): Promise<string> {
    const result = await this.http
      .post<ResponseEnvelope<CreateCheckoutSessionResponse>>(
        `${this.apiBaseUrl}/api/Payments/CreateCheckoutSession`,
        { planId }
      )
      .toPromise();

    if (result.resultStatus?.statusCode !== 1 || !result.responseData?.url) {
      throw new Error(
        result.resultStatus?.errorText || "Could not start checkout."
      );
    }

    return result.responseData.url;
  }

  /**
   * FLIP-263: opens the Stripe-hosted Billing Portal so the user can update their stored
   * card. Requires a Stripe Customer already linked via checkout (FLIP-249) - see
   * PaymentsController.CreateBillingPortalSession.
   */
  async openBillingPortal(): Promise<string> {
    const result = await this.http
      .post<ResponseEnvelope<CreateBillingPortalSessionResponse>>(
        `${this.apiBaseUrl}/api/Payments/CreateBillingPortalSession`,
        null
      )
      .toPromise();

    if (result.resultStatus?.statusCode !== 1 || !result.responseData?.url) {
      throw new Error(
        result.resultStatus?.errorText || "Could not open billing portal."
      );
    }

    return result.responseData.url;
  }

  async verifySession(sessionId: string): Promise<boolean> {
    const result = await this.http
      .get<ResponseEnvelope<VerifySessionResponse>>(
        `${this.apiBaseUrl}/api/Payments/VerifySession`,
        { params: { sessionId } }
      )
      .toPromise();

    return !!result.responseData?.verified;
  }

  async subscriptionStatus(): Promise<SubscriptionStatusResponse> {
    const result = await this.http
      .get<ResponseEnvelope<SubscriptionStatusResponse>>(
        `${this.apiBaseUrl}/api/Payments/SubscriptionStatus`
      )
      .toPromise();

    return result.responseData || { status: "none", isActive: false };
  }

  /**
   * Sets cancel_at_period_end on the caller's Stripe Subscription (FLIP-261) - access
   * continues until the current period ends. Throws with a user-facing message on
   * failure, matching createCheckoutSession's error convention.
   */
  async cancelSubscription(): Promise<CancelSubscriptionResponse> {
    const result = await this.http
      .post<ResponseEnvelope<CancelSubscriptionResponse>>(
        `${this.apiBaseUrl}/api/Payments/CancelSubscription`,
        null
      )
      .toPromise();

    if (result.resultStatus?.statusCode !== 1) {
      throw new Error(
        result.resultStatus?.errorText || "Could not cancel subscription."
      );
    }

    return result.responseData || {};
  }

  /**
   * Clears cancel_at_period_end on the caller's Stripe Subscription (FLIP-262) - the same
   * subscription simply continues, no new charge. Only valid before the period ends; once
   * a subscription has lapsed (status "canceled"), callers should go through
   * createCheckoutSession instead - see PaymentsController.ResumeSubscription.
   */
  async resumeSubscription(): Promise<ResumeSubscriptionResponse> {
    const result = await this.http
      .post<ResponseEnvelope<ResumeSubscriptionResponse>>(
        `${this.apiBaseUrl}/api/Payments/ResumeSubscription`,
        null
      )
      .toPromise();

    if (result.resultStatus?.statusCode !== 1) {
      throw new Error(
        result.resultStatus?.errorText || "Could not resume subscription."
      );
    }

    return result.responseData || {};
  }

  async billingHistory(): Promise<BillingHistoryItem[]> {
    const result = await this.http
      .get<ResponseEnvelope<BillingHistoryItem[]>>(
        `${this.apiBaseUrl}/api/Payments/BillingHistory`
      )
      .toPromise();

    return result.responseData || [];
  }

  /**
   * Anonymous email lookup backing the login page's "already started an account"
   * prompt - see AccountController.CheckAccountSubscription. Hand-written for the
   * same reason as the rest of this file (see class comment); returns booleans
   * only, never a UserGuid or token.
   */
  async checkAccountSubscription(email: string): Promise<CheckAccountSubscriptionResponse> {
    const result = await this.http
      .post<ResponseEnvelope<CheckAccountSubscriptionResponse>>(
        `${this.apiBaseUrl}/api/Account/CheckAccountSubscription`,
        null,
        { params: { EmailAddress: email } }
      )
      .toPromise();

    return result.responseData || { accountExists: false, hasActiveSubscription: false };
  }
}
